import 'leaflet/dist/leaflet.css';

import type { PlaceLocationRecord } from '@navio/nsdk';
import type { Map as LeafletMap } from 'leaflet';
import { cn } from 'noddi-ui-common';
import { NoddiLinearProgressLoader, useFullScreenBodyLock } from 'noddi-ui-web';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import type { MapLayerKey } from './constants';
import { INITIAL_CENTER, INITIAL_ZOOM, MAP_LAYERS, MAX_ZOOM, MIN_ZOOM } from './constants';
import { LeafletCurrentLocationMarker } from './LeafletCurrentLocationMarker';
import { LeafletMapControls } from './LeafletMapControls';
import { LeafletSearchMarker } from './LeafletSearchMarker';
import type { PlaceSearchTranslations } from './PlaceSearch';
import { useMapCurrentLocation } from './useMapCurrentLocation';

// Dont use this loading state on initial fetch, rather use <LoadingScreen /> from noddi-ui-web ( or else Oslo will always show at first..)
// This state is for updates in the map, only use case for now is the delivery /filter query page

export type CenterCoordinates = { latitude: number; longitude: number };

interface NoddiLeafletMapContainerProps {
  children: ReactNode;
  loading?: boolean;
  className?: string;
  containerClassName?: string;
  initialZoom?: number;
  showLayerToggle?: boolean;
  showPlaceSearch?: boolean;
  showCurrentLocation?: boolean;
  defaultLayer?: MapLayerKey;
  centerCoordinates?: CenterCoordinates;
  centerZoom?: number;
  onPlaceSelected?: (location: PlaceLocationRecord) => void;
  onPlaceClear?: () => void;
  placeSearchTranslations?: PlaceSearchTranslations;
  /** Off for a map embedded in a scrolling page, so the wheel scrolls the page instead of zooming. */
  scrollWheelZoom?: boolean;
  /** Adds a control that opens the map over the whole window. Not for a map inside a dialog. */
  allowFullScreen?: boolean;
}

export const NoddiLeafletMapContainer = ({
  children,
  loading,
  className,
  containerClassName,
  initialZoom,
  showLayerToggle = true,
  showPlaceSearch = true,
  showCurrentLocation = true,
  defaultLayer = 'light',
  centerCoordinates,
  centerZoom = 13,
  onPlaceSelected,
  onPlaceClear,
  placeSearchTranslations,
  scrollWheelZoom = true,
  allowFullScreen = false
}: NoddiLeafletMapContainerProps) => {
  const [activeLayer, setActiveLayer] = useState<MapLayerKey>(defaultLayer);
  const layer = MAP_LAYERS[activeLayer];
  const mapRef = useRef<LeafletMap | null>(null);
  const { isFullScreen, setIsFullScreen, ref: fullScreenRef } = useFullScreenBodyLock();

  // Leaflet measures its container once; tell it the size changed so tiles fill the new frame.
  useEffect(() => {
    mapRef.current?.invalidateSize();
  }, [isFullScreen]);

  const handleCenter = () => {
    if (mapRef.current && centerCoordinates) {
      mapRef.current.flyTo([centerCoordinates.latitude, centerCoordinates.longitude], centerZoom, { duration: 0.8 });
    }
  };

  const [selectedSearchLocation, setSelectedSearchLocation] = useState<PlaceLocationRecord | null>(null);

  const handlePlaceSelected = (location: PlaceLocationRecord) => {
    // Only complete addresses get a marker. Incomplete places such as cities still recenter the map.
    setSelectedSearchLocation(location.addressInfo ? location : null);
    mapRef.current?.flyTo([location.latitude, location.longitude], centerZoom, { duration: 0.8 });
    // Pages can still react to the raw place result for side effects such as address creation.
    void onPlaceSelected?.(location);
  };

  const handlePlaceClear = () => {
    setSelectedSearchLocation(null);
    onPlaceClear?.();
  };

  const { currentLocationCoordinates, locationState, handleCurrentLocation } = useMapCurrentLocation({ mapRef });

  const handleZoomIn = () => {
    mapRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
    mapRef.current?.zoomOut();
  };

  return (
    <div
      ref={fullScreenRef}
      className={cn('relative', containerClassName, isFullScreen && 'fixed inset-0 z-fullscreen bg-background p-4')}
    >
      {loading && (
        <div className='absolute -top-2 w-full'>
          <NoddiLinearProgressLoader />
        </div>
      )}
      <LeafletMapControls
        centerCoordinates={centerCoordinates}
        showCurrentLocation={showCurrentLocation}
        showLayerToggle={showLayerToggle}
        showPlaceSearch={showPlaceSearch}
        activeLayer={activeLayer}
        locationState={locationState}
        onCenter={handleCenter}
        onLayerChange={setActiveLayer}
        onPlaceClear={handlePlaceClear}
        onPlaceSelected={handlePlaceSelected}
        onCurrentLocation={handleCurrentLocation}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        placeSearchTranslations={placeSearchTranslations}
        fullScreen={allowFullScreen ? { isFullScreen, onToggle: () => setIsFullScreen(!isFullScreen) } : undefined}
      />

      {/* Leaflet reads the container's class once at mount, so the size lives on this wrapper. */}
      <div className={cn('h-150 overflow-hidden rounded-xl', className, isFullScreen && 'h-full')}>
        <MapContainer
          ref={mapRef}
          className='z-10 h-full'
          center={INITIAL_CENTER}
          zoom={initialZoom ?? INITIAL_ZOOM}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          attributionControl={false}
          zoomControl={false}
          scrollWheelZoom={scrollWheelZoom}
        >
          <TileLayer
            key={activeLayer}
            url={layer.url}
            tileSize={layer.tileSize}
            zoomOffset={layer.zoomOffset}
            maxNativeZoom={layer.maxNativeZoom}
          />
          {layer.referenceUrl && (
            <TileLayer
              key={`${activeLayer}-reference`}
              url={layer.referenceUrl}
              tileSize={layer.tileSize}
              zoomOffset={layer.zoomOffset}
              maxNativeZoom={layer.maxNativeZoom}
            />
          )}
          <LeafletCurrentLocationMarker coordinates={currentLocationCoordinates} />
          <LeafletSearchMarker location={selectedSearchLocation} />
          {children}
        </MapContainer>
      </div>
    </div>
  );
};
